import React from 'react';
import Seo from '../components/Seo';
import ContactForm from '../components/ContactForm';
import { Reveal, MaskLine, Eyebrow } from '../components/Reveal';
import { EMAIL, PHONE, ADDRESS, waLink } from '../data/content';
import { pageSeo, breadcrumbSchema } from '../data/seo';

const MAP_EMBED = 'https://www.google.com/maps?q=' + encodeURIComponent('Tekwash Systems Pvt Ltd, Ragad Shikshak Nagar, Kothrud, Pune 411038') + '&output=embed';
const MAP_LINK = 'https://www.google.com/maps/search/?api=1&query=Tekwash+Systems+Pvt+Ltd+Kothrud+Pune+411038';

function ContactHero() {
  return (
    <section className="relative pt-40 pb-16 blueprint-grid overflow-hidden" data-testid="contact-hero">
      <div className="absolute inset-0 bg-gradient-to-b from-ink/40 to-ink" aria-hidden="true" />
      <div className="mx-auto max-w-7xl px-5 lg:px-8 relative z-10">
        <Eyebrow className="mb-6 flex items-center gap-3">
          <span className="inline-block h-[1px] w-10 bg-flame" aria-hidden="true" /> Contact Us
        </Eyebrow>
        <h1 className="font-display font-extrabold text-paper leading-[0.98] tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
          <MaskLine delay={0.15}>DIRECT LINE TO</MaskLine>
          <MaskLine delay={0.3}><span className="text-flame">OUR ENGINEERS.</span></MaskLine>
        </h1>
        <p className="mt-8 max-w-2xl text-lg text-fog leading-relaxed">
          Share your component and cleanliness target — we respond with the right machine, process and a plan.
        </p>
      </div>
    </section>
  );
}

function ContactCards() {
  return (
    <>
      <Reveal testid="contact-card-email">
        <a href={`mailto:${EMAIL}`} data-testid="contact-email-card" className="block bg-surface/60 border border-steelline hover:border-flame/60 transition-colors p-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-flame">Email</p>
          <p className="mt-3 font-display font-bold text-lg text-paper">{EMAIL}</p>
        </a>
      </Reveal>
      <Reveal delay={0.1} testid="contact-card-phone">
        <div className="bg-surface/60 border border-steelline p-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-flame">Phone & WhatsApp</p>
          <a href={`tel:${PHONE.replace(/\s/g, '')}`} data-testid="contact-phone-link" className="mt-3 block font-display font-bold text-lg text-paper hover:text-flame transition-colors">{PHONE}</a>
          <a href={waLink('Hello Tekwash Systems, I have a component cleaning requirement.')} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="mt-4 inline-flex items-center gap-2 bg-[#25D366] text-white text-sm font-semibold px-5 py-2.5 hover:opacity-90 transition-opacity">
            Chat on WhatsApp
          </a>
        </div>
      </Reveal>
      <Reveal delay={0.2} testid="contact-card-address">
        <div className="bg-surface/60 border border-steelline p-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-flame">Registered Office</p>
          <p className="mt-3 text-paper leading-relaxed">Tekwash Systems Pvt Ltd<br />{ADDRESS}</p>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-fog">Production Facility — Pune, Maharashtra</p>
        </div>
      </Reveal>
    </>
  );
}

function ContactMap() {
  return (
    <Reveal delay={0.3}>
      <div className="clip-notch border border-steelline/80 overflow-hidden bg-surface" data-testid="contact-map">
        <iframe
          title="Tekwash Systems Pvt Ltd location — Kothrud, Pune"
          src={MAP_EMBED}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
          className="w-full aspect-[16/9] border-0 grayscale-[35%] contrast-[1.05]"
        />
      </div>
      <a href={MAP_LINK} target="_blank" rel="noopener noreferrer" data-testid="contact-map-link" className="mt-3 inline-block font-mono text-[11px] uppercase tracking-[0.2em] text-fog hover:text-flame transition-colors">
        Open in Google Maps →
      </a>
    </Reveal>
  );
}

export default function Contact() {
  return (
    <>
      <Seo {...pageSeo.contact} schemas={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Contact', path: '/contact' }])]} />
      <ContactHero />
      <section className="pb-24" data-testid="contact-body">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-5">
            <ContactCards />
            <ContactMap />
          </div>
          <Reveal delay={0.15} className="lg:col-span-7">
            <ContactForm />
          </Reveal>
        </div>
      </section>
    </>
  );
}
